// ============================================================
// PortalFactoryHome — the Olivium Project Portal Factory landing surface.
//
// Renders the reusable-infrastructure story: the template gallery, the
// module catalog by category (core / inherited / future, each with its
// honest status), and the two entry points (Create a portal, Open the
// registry). All content here is a labelled CATALOG — it is not fabricated
// portal/project data. Presentational + SSR-safe; the route wrapper wires
// navigation.
// ============================================================

import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import {
  PORTAL_TEMPLATES, MODULE_CATALOG, PROJECT_TYPES, PROJECT_TYPE_META,
  CLIENT_VISIBILITY_RULES, PORTAL_DESIGN_RULES,
  OLIVIUM_ACCENT, PORTAL_REGISTRY_ROOT, PORTAL_FACTORY_ROOT,
  type ModuleCategory,
} from './portalFactory'
import { PORTAL_HONESTY, ModuleStatusBadge, SectionCard, TemplateBadge, primaryBtn, chipBtn } from './PortalKit'

export interface PortalFactoryHomeProps {
  onHome?: () => void
  onBackCommandCenter?: () => void
  onCreatePortal?: () => void
  onOpenRegistry?: () => void
  onSelectTemplate?: (templateKey: string) => void
}

const CATEGORY_TITLE: Record<ModuleCategory, string> = {
  core: 'Core modules — portal-native',
  inherited: 'Inherited modules — attach the existing V2 spine',
  future: 'Future modules — deferred',
}

export default function PortalFactoryHome({ onHome, onBackCommandCenter, onCreatePortal, onOpenRegistry, onSelectTemplate }: PortalFactoryHomeProps) {
  const rightRail = (
    <div>
      <SectionCard title="What the factory is" accent={OLIVIUM_ACCENT}>
        <p style={{ margin: 0, fontSize: 12, color: CC_INK.secondary, lineHeight: 1.6 }}>
          Olivium as <strong>reusable infrastructure</strong>, not a one-off page. Register a project, apply an
          Olivium portal shell, attach modules, and track readiness, actions, notes, blockers, and receipts as a
          <strong> real saved record</strong> you can reopen later.
        </p>
      </SectionCard>
      <SectionCard title="Honesty" accent="#b8862a">
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: CC_INK.muted, lineHeight: 1.7 }}>
          <li>Portals persist to <code style={{ fontFamily: CC_MONO }}>sq_project_portals</code> — nothing fabricated.</li>
          <li>Migration <code style={{ fontFamily: CC_MONO }}>20260709000003</code> applied 2026-07-10 — portals, modules, actions, notes, blockers, and receipts are live records.</li>
          <li>Communications / Team / Daily Meetings are <strong>attached</strong> from the existing spine, not rebuilt.</li>
          <li>External client access and realtime are deferred.</li>
        </ul>
      </SectionCard>
      <SectionCard title="Design rules" accent={OLIVIUM_ACCENT}>
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10.5, color: CC_INK.muted, lineHeight: 1.6 }}>
          {PORTAL_DESIGN_RULES.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      </SectionCard>
    </div>
  )

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      breadcrumb={['OLIVIUM', 'PORTAL FACTORY']}
      title="Olivium Project Portal Factory"
      titleAccent={OLIVIUM_ACCENT}
      honestyBadge={PORTAL_HONESTY}
      contextBar={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
          <span style={{ fontSize: 11, color: CC_INK.muted, flex: 1 }}>
            Apply Olivium to any project — a saved portal shell with attachable modules.
          </span>
          <button type="button" style={chipBtn} onClick={onOpenRegistry}>▤ Portal registry</button>
          <button type="button" style={primaryBtn} onClick={onCreatePortal}>+ Create a project portal</button>
        </div>
      }
      rightRail={rightRail}
    >
      <div>
        {/* Templates */}
        <SectionCard
          title="Portal templates"
          hint="Templates define a recommended module set and shell copy. They do not fake live integrations or real project data."
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {PORTAL_TEMPLATES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => onSelectTemplate?.(t.key)}
                style={{ textAlign: 'left', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12, cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <strong style={{ fontSize: 12.5, color: CC_INK.primary, flex: 1 }}>{t.label}</strong>
                  <TemplateBadge label="TEMPLATE" />
                </div>
                <p style={{ margin: '0 0 8px', fontSize: 11, color: CC_INK.muted, lineHeight: 1.5 }}>{t.description}</p>
                <div style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>
                  {t.recommendedModules.length} recommended module{t.recommendedModules.length === 1 ? '' : 's'}
                </div>
              </button>
            ))}
          </div>
        </SectionCard>

        {/* Project types */}
        <SectionCard title="Project types" hint="A portal can be any of these project types.">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PROJECT_TYPES.map((pt) => (
              <span key={pt} title={PROJECT_TYPE_META[pt].blurb} style={{ ...chipBtn, cursor: 'help' }}>
                {PROJECT_TYPE_META[pt].label}
              </span>
            ))}
          </div>
        </SectionCard>

        {/* Client visibility rules — external access is deferred; nothing is
            client-visible by default. Made explicit + testable. */}
        <SectionCard title="Client visibility rules" hint="External client access is DEFERRED. Nothing below is client-visible by default — a client-visible update would require an explicit approval/publish step that does not exist yet." accent="#b8862a">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {CLIENT_VISIBILITY_RULES.map((r) => (
              <div key={r.kind} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <strong style={{ fontSize: 12, color: CC_INK.primary, flex: 1 }}>{r.label}</strong>
                  <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', fontFamily: CC_MONO, color: '#b8862a', padding: '3px 7px', borderRadius: 5, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)' }}>NOT CLIENT-VISIBLE</span>
                </div>
                <p style={{ margin: 0, fontSize: 10.5, color: CC_INK.muted, lineHeight: 1.5 }}>{r.rule}</p>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Module catalog by category */}
        {(['core', 'inherited', 'future'] as ModuleCategory[]).map((cat) => (
          <SectionCard key={cat} title={CATEGORY_TITLE[cat]}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
              {MODULE_CATALOG.filter((m) => m.category === cat).map((m) => (
                <div key={m.key} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <strong style={{ fontSize: 12, color: CC_INK.primary, flex: 1 }}>{m.label}</strong>
                    <ModuleStatusBadge status={m.status} />
                  </div>
                  <p style={{ margin: 0, fontSize: 11, color: CC_INK.muted, lineHeight: 1.5 }}>{m.purpose}</p>
                  {m.note && <p style={{ margin: '6px 0 0', fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{m.note}</p>}
                </div>
              ))}
            </div>
          </SectionCard>
        ))}

        <div style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO, marginTop: 4 }}>
          {PORTAL_FACTORY_ROOT} · saved portals at {PORTAL_REGISTRY_ROOT}
        </div>
      </div>
    </CommandCenterShell>
  )
}
